
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { CalendarIcon, Clock, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  author_avatar: string;
  date: string;
  category: string;
  read_time: string;
  image_url: string;
  table_of_contents: TableOfContentsItem[];
  meta_description: string;
  meta_keywords: string;
}

interface TableOfContentsItem {
  id: string;
  title: string;
  level: number;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  
  // For demo purposes, showing how to fetch a blog post
  // In a real application, this would fetch from the database
  const { data: post, isLoading, error } = useQuery({
    queryKey: ["blogPost", slug],
    queryFn: async () => {
      // This is a placeholder for demo purposes
      // Normally you would fetch from Supabase like this:
      // const { data, error } = await supabase
      //   .from("blog_posts")
      //   .select("*")
      //   .eq("slug", slug)
      //   .single();
      
      // For now, return mock data
      return {
        id: "post-1",
        title: "5 Common Mistakes to Avoid in Your College Application Essays",
        content: `
          <h2 id="introduction">Introduction</h2>
          <p>Writing college application essays can be daunting. These personal statements are often the only opportunity for admissions officers to hear your voice and understand who you are beyond your grades and test scores. However, many students make common mistakes that weaken their essays and diminish their chances of acceptance.</p>
          
          <p>In this article, we'll explore five common pitfalls in college application essays and provide actionable advice on how to avoid them.</p>
          
          <h2 id="mistake-1">Mistake #1: Writing What You Think Admissions Officers Want to Hear</h2>
          <p>One of the biggest mistakes students make is trying to craft an essay they believe will impress admissions officers rather than writing authentically about their experiences and perspectives.</p>
          
          <p>Admissions committees read thousands of essays each year, and they can spot insincerity from miles away. When students write what they think officers want to hear rather than sharing their genuine thoughts and experiences, the essay often comes across as generic and forgettable.</p>
          
          <h3 id="how-to-avoid">How to Avoid This Mistake</h3>
          <p>Focus on telling your unique story. Choose topics that genuinely matter to you, and don't shy away from showing vulnerability. Authentic essays that reveal your true character and perspective will always stand out more than those attempting to fit a perceived mold.</p>
          
          <h2 id="mistake-2">Mistake #2: Trying to Cover Too Much</h2>
          <p>Many students attempt to cram their entire life story into 650 words. This approach results in a superficial overview rather than a meaningful narrative that showcases depth of character.</p>
          
          <p>When you try to cover too much ground, you end up with a list of accomplishments rather than a compelling story that allows admissions officers to understand who you are.</p>
          
          <h3 id="how-to-focus">How to Focus Your Essay</h3>
          <p>Instead of trying to cover everything, choose one significant moment, experience, or aspect of your life and explore it in depth. This focused approach allows you to craft a more engaging narrative and provide meaningful insight into your character.</p>
          
          <h2 id="mistake-3">Mistake #3: Neglecting Structure and Flow</h2>
          <p>A well-structured essay guides readers through your thoughts seamlessly. Many students underestimate the importance of logical organization and transitions in their essays.</p>
          
          <p>Without proper structure, even the most interesting content can become confusing or difficult to follow, causing readers to lose interest or miss your key points.</p>
          
          <h2 id="mistake-4">Mistake #4: Overusing SAT Vocabulary</h2>
          <p>Some students believe that using sophisticated vocabulary will impress admissions officers. However, unnecessarily complex language often obscures your message and can make your writing seem pretentious or inauthentic.</p>
          
          <p>Remember that the goal of your essay is to communicate effectively, not to showcase your vocabulary.</p>
          
          <h2 id="mistake-5">Mistake #5: Failing to Proofread Carefully</h2>
          <p>Submitting an essay with grammatical errors, typos, or inconsistencies can signal to admissions officers that you lack attention to detail or don't care enough about your application.</p>
          
          <p>Even small mistakes can detract from an otherwise strong essay and leave a negative impression.</p>
          
          <h3 id="proofreading-tips">Proofreading Tips</h3>
          <p>After finishing your essay, set it aside for a day or two before reviewing it with fresh eyes. Read it aloud to catch awkward phrasing or errors that might be missed when reading silently. Consider asking teachers, counselors, or trusted friends to review your essay and provide feedback.</p>
          
          <h2 id="conclusion">Conclusion</h2>
          <p>College application essays provide a valuable opportunity to distinguish yourself from other applicants. By avoiding these common mistakes, you can craft an essay that authentically represents who you are and effectively communicates why you would be a valuable addition to the campus community.</p>
          
          <p>Remember, the best essays don't just tell admissions officers about your accomplishments—they show your character, your thinking process, and how you might contribute to their institution.</p>
        `,
        excerpt: "Learn the most common pitfalls that even strong students make when writing their application essays, and how to avoid them.",
        author: "Emily Johnson",
        author_avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=256&h=256",
        date: "2023-04-02T12:00:00Z",
        category: "Admissions",
        read_time: "7 min read",
        image_url: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=800&h=500",
        table_of_contents: [
          { id: "introduction", title: "Introduction", level: 2 },
          { id: "mistake-1", title: "Mistake #1: Writing What You Think Admissions Officers Want to Hear", level: 2 },
          { id: "how-to-avoid", title: "How to Avoid This Mistake", level: 3 },
          { id: "mistake-2", title: "Mistake #2: Trying to Cover Too Much", level: 2 },
          { id: "how-to-focus", title: "How to Focus Your Essay", level: 3 },
          { id: "mistake-3", title: "Mistake #3: Neglecting Structure and Flow", level: 2 },
          { id: "mistake-4", title: "Mistake #4: Overusing SAT Vocabulary", level: 2 },
          { id: "mistake-5", title: "Mistake #5: Failing to Proofread Carefully", level: 2 },
          { id: "proofreading-tips", title: "Proofreading Tips", level: 3 },
          { id: "conclusion", title: "Conclusion", level: 2 }
        ],
        meta_description: "Discover the 5 most common mistakes students make in college application essays and learn actionable tips to avoid them and strengthen your applications.",
        meta_keywords: "college application essays, application mistakes, college admissions tips, essay writing, college applications"
      };
    }
  });

  // Update page metadata for SEO
  useEffect(() => {
    if (post) {
      // Set meta tags for SEO
      document.title = `${post.title} | Alumni Network`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', post.meta_description);
      
      // Update keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', post.meta_keywords);
      
      // Open Graph tags for social sharing
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', post.title);
      
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', post.excerpt);
      
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute('content', post.image_url);
    }
    
    // Clean up meta tags on unmount
    return () => {
      document.title = "Alumni Network";
    };
  }, [post]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <NavBar />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="animate-pulse max-w-4xl mx-auto">
            <div className="h-8 bg-gray-200 w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 w-1/2 mb-8"></div>
            <div className="h-64 bg-gray-200 mb-8 rounded-lg"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="h-4 bg-gray-200"></div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <NavBar />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Article Not Found</h2>
            <p className="text-gray-600 mb-6">We couldn't find the article you're looking for.</p>
            <Button asChild className="bg-crimson hover:bg-crimson-dark">
              <Link to="/blog">Back to Blog</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <NavBar />
      
      <main className="flex-grow">
        {/* Hero Image */}
        <div 
          className="w-full h-[300px] md:h-[400px] bg-center bg-cover"
          style={{ backgroundImage: `url(${post.image_url})` }}
        >
          <div className="w-full h-full bg-black bg-opacity-40 flex items-center">
            <div className="container mx-auto px-4">
              <Button variant="outline" className="bg-white mb-4" asChild>
                <Link to="/blog">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Blog
                </Link>
              </Button>
              <h1 className="text-3xl md:text-5xl font-bold text-white font-playfair mb-4 max-w-4xl">
                {post.title}
              </h1>
              <div className="flex items-center text-white">
                <span className="bg-crimson px-3 py-1 rounded-full text-sm font-medium">
                  {post.category}
                </span>
                <span className="mx-4">·</span>
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {post.read_time}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Content Area */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Article Content */}
            <div className="lg:w-2/3">
              {/* Author Info */}
              <div className="flex items-center mb-8">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={post.author_avatar} alt={post.author} />
                  <AvatarFallback>{post.author.slice(0, 2)}</AvatarFallback>
                </Avatar>
                <div className="ml-4">
                  <p className="font-semibold text-gray-900">{post.author}</p>
                  <p className="text-sm text-gray-500">
                    {format(new Date(post.date), "MMMM d, yyyy")}
                  </p>
                </div>
              </div>
              
              {/* Article Content */}
              <article className="prose prose-lg max-w-none">
                <div dangerouslySetInnerHTML={{ __html: post.content }} />
              </article>
              
              {/* Tags */}
              <div className="mt-12">
                <h4 className="text-lg font-semibold mb-2">Related Topics</h4>
                <div className="flex flex-wrap gap-2">
                  {post.meta_keywords.split(',').map((keyword, index) => (
                    <span 
                      key={index}
                      className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm"
                    >
                      {keyword.trim()}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Sidebar with Table of Contents */}
            <div className="lg:w-1/3">
              <Card className="p-6 sticky top-24">
                <h3 className="text-lg font-bold mb-4">Table of Contents</h3>
                <ul className="space-y-3">
                  {post.table_of_contents.map(item => (
                    <li 
                      key={item.id}
                      className={`
                        ${item.level === 2 ? 'font-medium' : 'text-sm pl-4'}
                        hover:text-crimson transition-colors
                      `}
                    >
                      <a href={`#${item.id}`}>{item.title}</a>
                    </li>
                  ))}
                </ul>
                
                <Separator className="my-6" />
                
                {/* Share Links */}
                <div>
                  <h3 className="text-lg font-bold mb-4">Share This Article</h3>
                  <div className="flex space-x-4">
                    <Button variant="outline" size="sm">Twitter</Button>
                    <Button variant="outline" size="sm">Facebook</Button>
                    <Button variant="outline" size="sm">LinkedIn</Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPost;
